import React, { useState, useRef, useEffect } from 'react';
import styles from './AICompositionDialog.module.css';
import useTheme from 'config/useTheme';
import { useAIAddCompositionService } from '../../services/useAIAddCompositionService';
import { AIIcon } from 'assets/icons';
import Loader from 'components/shared/loader/Loader';

const SHORTCUTS = [
    {
        label: 'Inventory System',
        message: 'Create an inventory system with products, stock levels, and warehouse locations with nested tracking.',
        icon: (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
                <path d="M20 7h-4V4a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v3H4a2 2 0 0 0-2 2v11a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M12 12v6M9 15h6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
        )
    },
    {
        label: 'CRM System',
        message: 'Design a CRM system with contacts, companies, deals, and activity tracking.',
        icon: (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
                <path d="M3 21h18M5 21V7l7-4 7 4v14M9 21v-6h6v6M9 9h6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
        )
    },
    {
        label: 'User Profiles',
        message: 'Create a user profiles database with authentication data, preferences, and roles.',
        icon: (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
                <circle cx="12" cy="8" r="4" stroke="currentColor" strokeWidth="2"/>
                <path d="M4 20c0-4 4-6 8-6s8 2 8 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
        )
    },
    {
        label: 'Patient Records',
        message: 'Design a patient records system with medical history, appointments, and prescriptions.',
        icon: (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M14 2v6h6M12 18v-6M9 15h6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
        )
    }
];

function formatSchemaType(field) {
    if (field?.is_object) return "OBJECT";
    const t = (field?.type || "string").toUpperCase().replace(/\s/g, "_");
    return t;
}

function SchemaObjectBlock({ object }) {
    if (!object || (!object.fields && !object.name)) return null;
    const name = object.name || "Object";
    const fields = object.fields || [];
    const fieldRows = fields.map((f) => {
        const hasObj = !!(f.object && (f.object.name || f.object.fields)) || f.is_object;
        return { ...f, name: f.name, type: f.type, is_object: hasObj, object: f.object };
    });
    return (
        <div className={styles.schema_nested}>
            <div className={styles.schema_nested_name}>{name}</div>
            {object.description && (
                <div className={styles.schema_nested_desc}>{object.description}</div>
            )}
            <div className={styles.schema_fields_label}>FIELDS</div>
            {fieldRows.map((row, i) => (
                <div key={i}>
                    <div className={styles.schema_field_row}>
                        <span className={styles.schema_field_name}>{row.name}</span>
                        <span
                            className={
                                row.is_object
                                    ? `${styles.schema_type_chip} ${styles.schema_type_chip_object}`
                                    : styles.schema_type_chip
                            }
                        >
                            {formatSchemaType(row)}
                        </span>
                    </div>
                    {row.is_object && row.object && (
                        <SchemaObjectBlock object={row.object} />
                    )}
                </div>
            ))}
        </div>
    );
}

function SchemaPreviewCard({ structure, className = "" }) {
    if (!Array.isArray(structure) || structure.length === 0) return null;
    return (
        <div className={`${styles.schema_card} ${className}`.trim()}>
            {structure.map((comp, idx) => {
                const indexRows = (comp.indexes || []).map((ind) => ({
                    name: ind.name || "id",
                    type: ind.type || "uint64_t",
                    is_object: false,
                    object: null
                }));
                const fieldRows = (comp.fields || []).map((f) => {
                    const hasObj = !!(f.object && (f.object.name || f.object.fields)) || f.is_object;
                    return {
                        name: f.name,
                        type: f.type,
                        is_object: hasObj,
                        object: f.object || null
                    };
                });
                const rows = [...indexRows, ...fieldRows];
                return (
                    <div key={idx} className={styles.schema_comp}>
                        <div className={styles.schema_comp_name}>{comp.name || "Unnamed"}</div>
                        {comp.description && (
                            <div className={styles.schema_comp_desc}>{comp.description}</div>
                        )}
                        <div className={styles.schema_fields_label}>FIELDS</div>
                        {rows.map((row, i) => (
                            <div key={i}>
                                <div className={styles.schema_field_row}>
                                    <span className={styles.schema_field_name}>{row.name}</span>
                                    <span
                                        className={
                                            row.is_object
                                                ? `${styles.schema_type_chip} ${styles.schema_type_chip_object}`
                                                : styles.schema_type_chip
                                        }
                                    >
                                        {formatSchemaType(row)}
                                    </span>
                                </div>
                                {row.is_object && row.object && (
                                    <SchemaObjectBlock object={row.object} />
                                )}
                            </div>
                        ))}
                    </div>
                );
            })}
        </div>
    );
}

const AICompositionDialog = ({ open, onClose, onConfirm, sessionId = null, onSessionIdChange = () => {} }) => {
    const { dark } = useTheme();
    const { sendMessage, loading, error, resetError, initialMessage } = useAIAddCompositionService();
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState('');
    const [latestStructure, setLatestStructure] = useState(null);
    const messagesEndRef = useRef(null);
    const inputRef = useRef(null);

    const scrollToBottom = () => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });

    useEffect(() => {
        if (open) {
            setMessages([{ role: 'bot', text: initialMessage }]);
            setLatestStructure(null);
            setInput('');
            resetError();
            setTimeout(() => inputRef.current?.focus(), 100);
        }
    }, [open, initialMessage, resetError]);

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    const sendDraft = async (text) => {
        if (!text || loading) return;
        setMessages((prev) => [...prev, { role: 'user', text }]);
        const history = messages
            .filter((m) => m.role === 'user' || m.role === 'bot')
            .map((m) => ({ role: m.role, content: m.text }));
        const { message, structure, sessionId: nextSessionId } = await sendMessage(text, history, sessionId);
        if (nextSessionId) {
            onSessionIdChange(nextSessionId);
        }
        if (message) {
            const displayText = typeof message === "string" && structure?.length
                ? message.replace(/\[SCHEMA_JSON\][\s\S]*?\[\/SCHEMA_JSON\]/g, "").trim() || message
                : message;
            setMessages((prev) => [
                ...prev,
                { role: "bot", text: displayText, structure: structure && Array.isArray(structure) ? structure : undefined }
            ]);
        }
        if (structure && Array.isArray(structure)) {
            setLatestStructure(structure);
        }
    };

    const handleSend = async () => {
        const text = input.trim();
        if (!text || loading) return;
        setInput('');
        await sendDraft(text);
    };

    const handleShortcutClick = (shortcut) => {
        if (loading) return;
        sendDraft(shortcut.message);
    };

    const handleConfirm = () => {
        if (latestStructure && latestStructure.length > 0) {
            onConfirm(latestStructure);
            onClose();
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    if (!open) return null;

    return (
        <>
            <div className={styles.overlay} onClick={onClose} aria-hidden="true" />
            <div className={`${styles.drawer} ${dark ? styles.dark : ''}`}>
                <div className={styles.header}>
                    <div className={styles.header_title}>
                        <AIIcon color={dark ? '#e0e0e0' : '#4D4D4D'} />
                        <span>AI Database Architect</span>
                    </div>
                    <button type="button" className={styles.close_btn} onClick={onClose} aria-label="Close">
                        ×
                    </button>
                </div>
                <div className={styles.messages}>
                    {messages.map((m, i) => (
                        <div key={i} className={m.role === "user" ? styles.msg_user : styles.msg_bot}>
                            {m.role === "bot" && (
                                <div className={styles.bot_avatar}><AIIcon color="#8294A1" /></div>
                            )}
                            <div className={styles.msg_content}>
                                {m.text && <div className={styles.msg_bubble}>{m.text}</div>}
                                {m.structure && (
                                    <SchemaPreviewCard
                                        structure={m.structure}
                                        className={styles.schema_card_in_msg}
                                    />
                                )}
                            </div>
                        </div>
                    ))}
                    {loading && (
                        <div className={styles.msg_bot}>
                            <div className={styles.bot_avatar}><AIIcon color="#8294A1" /></div>
                            <div className={styles.msg_bubble_loader}>
                                <Loader />
                            </div>
                        </div>
                    )}
                    {messages.length === 1 && !loading && (
                        <div className={styles.shortcuts}>
                            {SHORTCUTS.map((s) => (
                                <button
                                    key={s.label}
                                    type="button"
                                    className={styles.shortcut_btn}
                                    onClick={() => handleShortcutClick(s)}
                                    disabled={loading}
                                >
                                    <span className={styles.shortcut_icon}>{s.icon}</span>
                                    <span>{s.label}</span>
                                </button>
                            ))}
                        </div>
                    )}
                    <div ref={messagesEndRef} />
                </div>
                {error && (
                    <div className={styles.error}>{error}</div>
                )}
                <div className={styles.footer}>
                    <textarea
                        ref={inputRef}
                        className={styles.input}
                        placeholder="e.g. Create a warehouse system with products and nested location tracking..."
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        onKeyDown={handleKeyDown}
                        rows={2}
                        disabled={loading}
                    />
                    <div className={styles.footer_actions}>
                        <button
                            type="button"
                            className={styles.send_btn}
                            onClick={handleSend}
                            disabled={!input.trim() || loading}
                        >
                            Send
                        </button>
                        <button
                            type="button"
                            className={styles.confirm_btn}
                            onClick={handleConfirm}
                            disabled={!latestStructure || latestStructure.length === 0}
                        >
                            Use this structure
                        </button>
                    </div>
                </div>
            </div>
        </>
    );
};

export default AICompositionDialog;
